const UNCONFIRMED = "状态未确认，请查看下载记录，勿重复提交";

function copy(value) {
  if (Array.isArray(value)) return value.map(copy);
  if (value && typeof value === "object") {
    return Object.keys(value).reduce((result, key) => {
      result[key] = copy(value[key]);
      return result;
    }, {});
  }
  return value;
}

// Purchases remain recorded for this page lifetime, including after cancellation.
export function createWaitDownloadFlow({
  submit,
  check,
  getSession,
  setTimer = (fn, delay) => setTimeout(fn, delay),
  clearTimer = (timer) => clearTimeout(timer),
  delayMs = 4000,
  maxChecks = 50,
  maxErrors = 5,
}) {
  const tasks = new Map();
  const skipped = {};
  let session = getSession();
  let disposed = false;

  const terminal = (task) =>
    task.value.state === "success" || task.value.state === "failed";
  const snapshot = (task) => ({ ...copy(task.value), params: copy(task.params) });

  function pause(task) {
    ++task.generation;
    task.active = false;
    if (task.timer !== null) clearTimer(task.timer);
    task.timer = null;
    if (!terminal(task))
      task.value = { state: "unconfirmed", message: UNCONFIRMED };
  }

  function cancelAll() {
    tasks.forEach((task) => {
      task.subscribers.forEach((subscriber) => {
        subscriber.active = false;
      });
      task.subscribers.clear();
      pause(task);
    });
  }

  function currentSession() {
    const current = getSession();
    if (current !== session) {
      cancelAll();
      session = current;
    }
    return current;
  }

  function valid(task, generation) {
    return (
      !disposed &&
      task.session === currentSession() &&
      task.generation === generation
    );
  }

  function notify(task, callback, only) {
    const generation = task.generation;
    const cycle = task.cycle;
    const subscribers = only ? [only] : Array.from(task.subscribers);
    subscribers.forEach((subscriber) => {
      if (!subscriber.active || !valid(task, generation)) return;
      if (callback === "onSettled") {
        if (subscriber.settledCycle === cycle) return;
        subscriber.settledCycle = cycle;
      }
      const listener = subscriber.callbacks[callback];
      if (typeof listener === "function") {
        // A view callback cannot turn a completed read into another request.
        try {
          listener(snapshot(task));
        } catch (_) {
          /* Keep other subscribers alive. */
        }
      }
    });
  }

  function update(task, state, data, message) {
    task.value = { state };
    if (data !== undefined) task.value.data = copy(data);
    if (message) task.value.message = message;
    notify(task, "onState");
  }

  function settle(task) {
    task.active = false;
    if (task.timer !== null) clearTimer(task.timer);
    task.timer = null;
    notify(task, "onSettled");
  }

  function unknown(task) {
    ++task.errors;
    update(task, "unconfirmed", undefined, UNCONFIRMED);
    if (task.errors >= maxErrors) settle(task);
  }

  function receive(task, data) {
    if (
      !data ||
      typeof data !== "object" ||
      Array.isArray(data) ||
      (data.code !== undefined && data.code !== 0)
    ) {
      unknown(task);
      return;
    }
    const status = data.status;
    if (
      (status === 0 || status === "0") &&
      typeof data.downloadUrl === "string" &&
      data.downloadUrl.trim()
    ) {
      update(task, "success", data);
      settle(task);
    } else if (status === 2 || status === "2") {
      const message = typeof data.msg === "string" ? data.msg : data.message;
      update(
        task,
        "failed",
        data,
        typeof message === "string" ? message : "下载失败，请联系客服"
      );
      settle(task);
    } else if (status === 1 || status === "1") {
      task.errors = 0;
      if (++task.pendingChecks >= maxChecks) {
        update(task, "unconfirmed", undefined, UNCONFIRMED);
        settle(task);
      } else {
        update(task, "pending");
      }
    } else {
      unknown(task);
    }
  }

  function schedule(task) {
    if (
      !valid(task, task.generation) ||
      !task.active ||
      terminal(task) ||
      task.timer !== null ||
      task.queryRunning ||
      task.submitRunning
    )
      return;
    const generation = task.generation;
    task.timer = setTimer(() => {
      task.timer = null;
      if (!valid(task, generation) || !task.active) return;
      task.queryRunning = true;
      const finish = () => {
        task.queryRunning = false;
        schedule(task);
      };
      Promise.resolve()
        .then(() => {
          return valid(task, generation) && task.active
            ? check(copy(task.params))
            : skipped;
        })
        .then(
          (data) => {
            if (data !== skipped && valid(task, generation) && task.active)
              receive(task, data);
          },
          () => {
            if (valid(task, generation) && task.active) unknown(task);
          }
        )
        .then(finish, finish);
    }, delayMs);
  }

  function resumeCycle(task) {
    ++task.generation;
    ++task.cycle;
    task.active = true;
    task.errors = 0;
    task.pendingChecks = 0;
    notify(task, "onState");
    schedule(task);
  }

  function subscribe(task, callbacks) {
    const subscriber = {
      callbacks: callbacks || {},
      active: true,
      settledCycle: -1,
    };
    task.subscribers.add(subscriber);
    const handle = {
      cancel() {
        if (!subscriber.active) return;
        subscriber.active = false;
        task.subscribers.delete(subscriber);
        if (!task.subscribers.size) pause(task);
      },
      resume() {
        if (disposed || task.session !== currentSession()) return handle;
        subscriber.active = true;
        task.subscribers.add(subscriber);
        if (terminal(task)) {
          notify(task, "onState", subscriber);
          notify(task, "onSettled", subscriber);
        } else if (!task.active) {
          resumeCycle(task);
        } else {
          notify(task, "onState", subscriber);
        }
        return handle;
      },
      getState() {
        currentSession();
        return snapshot(task);
      },
    };
    return { handle, subscriber };
  }

  function open(params, callbacks, shouldSubmit) {
    const capturedSession = currentSession();
    if (disposed) {
      const handle = {
        cancel() {},
        resume: () => handle,
        getState: () => ({ state: "unconfirmed", message: UNCONFIRMED }),
      };
      return handle;
    }
    // Legacy /check has no format or isCos in its task identity.
    const key = JSON.stringify([
      capturedSession,
      String(params.imageID),
      params.webType,
    ]);
    let task = tasks.get(key);
    if (task) {
      const { handle, subscriber } = subscribe(task, callbacks);
      if (terminal(task)) {
        notify(task, "onState", subscriber);
        notify(task, "onSettled", subscriber);
      } else if (!task.active) {
        resumeCycle(task);
      } else {
        notify(task, "onState", subscriber);
      }
      return handle;
    }
    task = {
      session: capturedSession,
      params: copy(params),
      subscribers: new Set(),
      generation: 1,
      cycle: 1,
      active: true,
      timer: null,
      queryRunning: false,
      submitRunning: shouldSubmit,
      errors: 0,
      pendingChecks: 0,
      value: shouldSubmit
        ? { state: "submitting" }
        : { state: "unconfirmed", message: UNCONFIRMED },
    };
    tasks.set(key, task);
    const { handle } = subscribe(task, callbacks);
    notify(task, "onState");
    if (shouldSubmit) {
      const generation = task.generation;
      const finish = () => {
        task.submitRunning = false;
        schedule(task);
      };
      Promise.resolve()
        .then(() => {
          return valid(task, generation) && task.active
            ? submit(copy(task.params))
            : skipped;
        })
        .then(
          (result) => {
            // buy_wait acknowledges with R.ok(message), which unwraps to undefined.
            if (result !== skipped && valid(task, generation) && task.active)
              update(task, "pending");
          },
          () => {
            if (valid(task, generation) && task.active) {
              update(task, "unconfirmed", undefined, UNCONFIRMED);
              settle(task);
            }
          }
        )
        .then(finish, finish);
    } else {
      schedule(task);
    }
    return handle;
  }

  return {
    start: (params, callbacks) => open(params, callbacks, true),
    resume: (params, callbacks) => open(params, callbacks, false),
    cancelAll,
    dispose() {
      cancelAll();
      disposed = true;
    },
  };
}
